import { exampleSubZoneResponse, stringSubZoneResponse } from "@/data/zone/example_sub_zone_polygon";
import type {
  ZoneApiItem,
  SubZoneApiItem,
  ZoneModel,
  SubZoneModel,
  ZonePolygonFeature,
  SubZonePolygonFeature,
} from "@/models/classes/zone";
import { ZoneApi } from "@/services/api/plan/zone";

const parseZoneCoordinates = (
  rawCoordinates?: ZoneApiItem["zoneCordinates"],
): ZonePolygonFeature | undefined => {
  if (!rawCoordinates) {
    return undefined;
  }

  if (typeof rawCoordinates !== "string") {
    return rawCoordinates;
  }

  try {
    const parsed = JSON.parse(rawCoordinates) as ZonePolygonFeature;
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch (_error) {
    return undefined;
  }
};

const safeParseGeoJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return undefined;
  }
};

const isSubZonePolygonFeature = (value: unknown): value is SubZonePolygonFeature => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const feature = value as { type?: unknown; geometry?: unknown };
  if (feature.type !== "Feature") {
    return false;
  }

  const geometry = feature.geometry as { type?: unknown; coordinates?: unknown } | undefined;
  if (!geometry || typeof geometry !== "object") {
    return false;
  }

  const { type, coordinates } = geometry as { type?: unknown; coordinates?: unknown };
  if (type !== "Polygon" && type !== "MultiPolygon") {
    return false;
  }

  return Array.isArray(coordinates);
};

const parseSubZoneCoordinates = (
  rawCoordinates?: SubZoneApiItem["subZoneCordinates"],
): SubZonePolygonFeature | undefined => {
  if (!rawCoordinates) {
    return undefined;
  }

  const candidate =
    typeof rawCoordinates === "string"
      ? safeParseGeoJson(rawCoordinates.trim())
      : rawCoordinates;

  if (!candidate) {
    return undefined;
  }

  return isSubZonePolygonFeature(candidate) ? candidate : undefined;
};


const api = new ZoneApi();

export class ZoneService {
  fetchZonePolygon = async (): Promise<ZoneModel[]> => {
    const response = await api.getZoneList();
    const zones: ZoneApiItem[] = (response?.data ?? []) as ZoneApiItem[];
    return zones
      .map((zone) => {
        const parsedCoordinates = parseZoneCoordinates(
          zone.zoneCordinates ?? (zone as any).zoneCoordinates,
        );

        const coordinates = parsedCoordinates;
        if (!coordinates) {
          return undefined;
        }

        return {
          zoneId: zone.zoneID,
          zoneName: zone.zoneName,
          province: zone.province,
          zoneCode: zone.zoneCode,
          isInbound: zone.isInbound,
          coordinates,
        };
      })
      .filter((item): item is ZoneModel => Boolean(item));
  };

  fetchSubZonePolygon = async (zoneId: number): Promise<SubZoneModel[]> => {
    const response = await api.getSubZoneList(zoneId);
    const subZones: SubZoneApiItem[] = (response?.data ?? []) as SubZoneApiItem[];
    return subZones
      .map((subZone) => {
        const rawCoordinates = subZone.subZoneCordinates ?? (subZone as any).subZoneCoordinates;
        const coordinates = parseSubZoneCoordinates(rawCoordinates);
        if (!coordinates) {
          return undefined;
        }

        return {
          subZoneId: subZone.subZoneId,
          subZoneName: subZone.subZoneName,
          subZoneCode: subZone.subZoneCode,
          isInbound: subZone.isInbound,
          coordinates,
        };
      })
      .filter((item): item is SubZoneModel => Boolean(item));
  };
}
